import React, { useState, useEffect, useContext } from 'react';
import { withStyles } from '@material-ui/core/styles';
import { withRouter } from 'react-router-dom';
import {
  Paper,
  Typography,
  Chip,
  Card,
  CardContent,
  CardHeader,
  Button,
  CircularProgress,
  Grid,
  TextField,
} from '@material-ui/core';
import classnames from 'classnames';
import * as Yup from 'yup';
import { ErrorMessage, Form, Formik, useField } from 'formik';
import { AppContext } from '../../../AppContext';
import { getFieldManager, updateFieldManager } from '../../../services';

const styles = theme => ({
  paper: { padding: '24px', boxSizing: 'border-box' },
  statesCard: {
    marginTop: 24,
    textAlign: 'left',
  },
  cardHeader: {
    paddingBottom: 0,
  },
  cardContent: {
    paddingTop: 16,
    paddingBottom: '24px !important',
  },
  formTextFieldInput: {
    padding: 15,
  },
  formTextFieldNotchedOutline: {
    '& fieldset': {
      borderColor: 'var(--jmi-blue) !important',
    },
  },
  errorMessage: {
    color: theme.palette.secondary.main,
    textAlign: 'center',
  },
  actionButtonWrap: {
    width: '100%',
    textAlign: 'center',
    marginTop: 40,
  },
  saveButtonWrap: {
    width: '100%',
    textAlign: 'center',
    marginTop: 40,
  },
  pills: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  statePill: {
    backgroundColor: 'var(--jmi-blue)',
    color: '#fff',
  },
  emptyStates: {
    color: 'rgba(0, 0, 0, 0.5)',
  },
});

const FormTextField = withStyles(styles)(({ classes, inputProps, InputProps, label, ...props }) => {
  const { name, onChange } = props;
  const [field] = useField(props);

  return (
    <TextField
      name={name}
      label={label}
      autoComplete={name}
      margin="none"
      variant="outlined"
      fullWidth
      inputProps={{
        ...inputProps,
        className: classes.formTextFieldInput,
      }}
      InputProps={{
        ...InputProps,
        classes: {
          ...InputProps?.classes,
          focused: classes.formTextFieldNotchedOutline,
        },
      }}
      {...props}
      {...field}
      onChange={onChange || field.onChange}
    />
  );
});

const validationSchema = Yup.object({
  name: Yup.string().required('Required'),
  userName: Yup.string(),
});

export const FieldManagerUsersDetail = withRouter(
  withStyles(styles)(({ classes, match, history }) => {
    const { userId } = match.params;
    const { dispatch } = useContext(AppContext);
    const [formData, setFormData] = useState(null);
    const [assignedStates, setAssignedStates] = useState([]);
    const [formError, setFormError] = useState('');

    useEffect(() => {
      const loadFieldManager = async () => {
        try {
          const manager = await getFieldManager(userId);
          const states = (manager.fieldManagerStateXref || [])
            .map(x => ({
              stateId: x.stateId ?? x.state?.stateId,
              label: x.state?.name || x.state?.abv || String(x.stateId ?? x.state?.stateId ?? ''),
            }))
            .filter(s => s.stateId != null)
            .sort((a, b) => a.label.localeCompare(b.label));

          setAssignedStates(states);
          setFormData({
            fieldManagerId: manager.fieldManagerId,
            name: manager.name || '',
            userName: manager.userName || '',
            fieldManagerStateXref: states.map(s => ({ stateId: s.stateId })),
          });
        } catch (err) {
          console.log(err);
          setFormError('Error loading field manager data');
        }
      };

      loadFieldManager();
    }, [userId]);

    const handleSubmit = async (values, { setSubmitting }) => {
      try {
        await updateFieldManager(
          {
            fieldManagerId: formData.fieldManagerId,
            name: values.name,
            userName: values.userName || '',
            fieldManagerStateXref: formData.fieldManagerStateXref || [],
          },
          { syncAssignments: false },
        );
        dispatch({
          type: 'SET_SNACKBAR',
          snackbar: {
            isOpen: true,
            variant: 'success',
            message: 'Field manager updated!',
          },
        });
        history.push('/users/field-managers');
      } catch (error) {
        dispatch({
          type: 'SET_SNACKBAR',
          snackbar: {
            isOpen: true,
            variant: 'error',
            message: error.message || 'Failed to update field manager',
          },
        });
      } finally {
        setSubmitting(false);
      }
    };

    const goToAssignmentPage = () => {
      history.push({
        pathname: '/settings/field-manager-assignment',
        search: `?fieldManagerId=${formData.fieldManagerId}`,
      });
    };

    if (formError) {
      return <div>{formError}</div>;
    }

    if (!formData) {
      return null;
    }

    return (
      <div>
        <Paper className={classnames(classes.paper, 'border-left-green')} elevation={1}>
          <Formik
            initialValues={{ name: formData.name, userName: formData.userName }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
            validateOnMount
          >
            {formik => (
              <Form>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormTextField name="name" label="Name" required />
                    <span className={classes.errorMessage}>
                      <ErrorMessage name="name" />
                    </span>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormTextField name="userName" label="Username" />
                    <span className={classes.errorMessage}>
                      <ErrorMessage name="userName" />
                    </span>
                  </Grid>
                </Grid>

                <div className={classes.saveButtonWrap}>
                  {formik.isSubmitting ? (
                    <CircularProgress size={24} />
                  ) : (
                    <Button
                      type="submit"
                      size="medium"
                      color="primary"
                      variant="outlined"
                      disabled={!formik.isValid || formik.isSubmitting}
                    >
                      Save
                    </Button>
                  )}
                </div>
              </Form>
            )}
          </Formik>
        </Paper>

        <Card className={classnames(classes.statesCard, 'border-left-green')} elevation={1}>
          <CardHeader
            className={classes.cardHeader}
            title={<Typography variant="subtitle1">Assigned States</Typography>}
          />
          <CardContent className={classes.cardContent}>
            {assignedStates.length === 0 ? (
              <Typography className={classes.emptyStates}>No states assigned</Typography>
            ) : (
              <div className={classes.pills}>
                {assignedStates.map(state => (
                  <Chip key={state.stateId} label={state.label} className={classes.statePill} />
                ))}
              </div>
            )}
            <div className={classes.actionButtonWrap}>
              <Button size="medium" color="primary" variant="outlined" onClick={goToAssignmentPage}>
                Edit
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }),
);
